import type { SimulationTest } from '@k-ucap/shared';
import type { SimulationTestRepository } from './RepositoryInterfaces.js';

export class InMemorySimulationTestRepository implements SimulationTestRepository {
  private tests: Map<string, SimulationTest> = new Map();

  async findAll(): Promise<SimulationTest[]> {
    return Array.from(this.tests.values()).sort((a, b) => a.name.localeCompare(b.name));
  }

  async findById(id: string): Promise<SimulationTest | undefined> {
    return this.tests.get(id);
  }

  async findByProjectId(projectId: string): Promise<SimulationTest[]> {
    return Array.from(this.tests.values())
      .filter((t) => t.projectId === projectId)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  async save(test: SimulationTest): Promise<SimulationTest> {
    this.tests.set(test.id, test);
    return test;
  }

  async delete(id: string): Promise<void> {
    this.tests.delete(id);
  }

  async duplicate(id: string, newName?: string): Promise<SimulationTest | undefined> {
    const original = this.tests.get(id);
    if (!original) return undefined;
    const duplicated: SimulationTest = {
      ...original,
      id: `${original.id}-copy-${Date.now()}`,
      name: newName || `${original.name} (Copy)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    this.tests.set(duplicated.id, duplicated);
    return duplicated;
  }

  seed(tests: SimulationTest[]): void {
    tests.forEach((t) => this.tests.set(t.id, t));
  }
}

export const createInMemorySimulationTestRepository = (): InMemorySimulationTestRepository =>
  new InMemorySimulationTestRepository();
